import React, { useContext, useEffect, useMemo, useState } from "react";
import "./WhaleDolphin.scss";
import { MapContext } from "../../../../context/MapContext";
import useQueryString from "../../../../hooks/useQueryString";
import {
  WhaleDolphinConfig,
  ReferenceConfig,
  refLayersToInclude,
} from "../WildLifeTourismConfig";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import QueryTask from "@arcgis/core/tasks/QueryTask";
import Query from "@arcgis/core/tasks/support/Query";
import WhaleDolphinStats from "./WhaleDolphinStats/WhaleDolphinStats";
import ReferenceLayers from "../../../ReferenceLayers/ReferenceLayers";
import utils from "../../../../helpers/utils";
import References from "../../../References/References";

function WhaleDolphin(props) {
  const { selectedGeometry, isGeoScaled, geoSelections } = props;
  const [mapObject] = useContext(MapContext);

  const [stats, setStats] = useState({});
  const [refLayersEnabled, setRefLayersEnabled] = useQueryString(
    "wdRefLayers",
    []
  );

  /**
   * Every time this route is accessed, make sure the EEZ layer reflects the correct boundaries
   */
  useEffect(() => {
    const eez = mapObject?.map.findLayerById("eez");
    if (eez) {
      eez.definitionExpression = `Dissolve in (${geoSelections
        .map((c) => "'" + utils.getCountryForEEZ(c) + "'")
        .join(",")})`;
    }
  }, [mapObject, geoSelections]);

  /**
   * Function that looks up layer object from config file to use based on current
   * app selections (selected geometry)
   */
  const getLayerForSelections = (geometry, isScaled) => {
    const dataSet = WhaleDolphinConfig.data;
    if (isScaled) {
      return dataSet.find((country) => country.name === geometry);
    }
    return dataSet.find((country) => country.name === "CROP Countries");
  };

  /**
   * Only one model layer is active at a time. Memoize it and update when user selections change
   */
  const layer = useMemo(() => {
    const dataLayer = getLayerForSelections(selectedGeometry, isGeoScaled);
    if (dataLayer) {
      return new FeatureLayer({
        url: dataLayer.url,
        title: dataLayer.name + " - Whale and Dolphin Watching",
      });
    }

    return null;
  }, [selectedGeometry, isGeoScaled]);

  /**
   * When the memoized layer object updates, we need to remove the current layer
   */
  useEffect(() => {
    if (layer) {
      mapObject?.map.remove(layer);
    }
  }, [layer, mapObject?.map]);

  /**
   * Second effect declared to add the new layer. Runs after first efect that removes. Has callback for removing on component unmount
   */
  useEffect(() => {
    if (layer) {
      mapObject?.map.add(layer);

      return () => {
        mapObject?.map.remove(layer);
      };
    }
  }, [mapObject?.map, layer]);

  /**
   * On component mount, fetch the stats table and prepare a query tasks to execute queries on
   */
  const queryTable = useMemo(() => {
    const crtTable = WhaleDolphinConfig.data.find(
      (crt) => crt.name === "Statistics Table"
    );
    return new QueryTask({ url: crtTable.url });
  }, []);

  /**
   * Fetch the stats for the currrent app and set them in state.
   */
  useEffect(() => {
    const query = new Query();
    query.returnGeometry = false;
    query.outFields = ["*"];
    query.where = "App = 'Whale and Dolphin Watching'";

    queryTable.execute(query).then((results) => {
      const newStats = {};
      results.features.forEach((feature) => {
        newStats[feature.attributes.Statistic] = feature.attributes;
      });
      setStats(newStats);
    });
  }, [queryTable, setStats]);

  /**
   * Toggles the visibility of a reference layer by adding/removing from an array that tracks currently visible ref layers
   */
  const handleRefLayerToggle = (layer) => {
    if (refLayersEnabled.includes(layer)) {
      //turn off
      setRefLayersEnabled([]);
    } else {
      //turn on
      setRefLayersEnabled([layer]);
    }
  };

  return (
    <div className="whaleDolphin">
      <div className="quickStats">
        <h3>Quick Stats for {selectedGeometry}</h3>
        <WhaleDolphinStats stats={stats} selectedGeometry={selectedGeometry} />
      </div>
      <div className="otherMaps">
        <h3>Additional Map Layers:</h3>
        <ReferenceLayers
          isRadio
          layersToInclude={refLayersToInclude.whaleDolphin}
          layersEnabled={refLayersEnabled}
          handleLayerToggle={handleRefLayerToggle}
        />
      </div>
      {ReferenceConfig && (
        <div className="references-wrapper">
          <h3>References</h3>
          <References refs={ReferenceConfig.whale} />
        </div>
      )}
    </div>
  );
}

export default WhaleDolphin;
